import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@components/ui/popover';
import { Button } from '@components/ui/button';
import { cn } from '@lib/utils';

export default function MultiSelect({
  label,
  options,
  selected,
  onChange,
  className,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const summary =
    selected.length === 0 ? label : selected.length === 1 ? selected[0] : `${label}: ${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={label}
          className={cn('w-full justify-between font-normal', selected.length === 0 && 'text-muted-foreground', className)}
        >
          <span className="truncate">{summary}</span>
          <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-56 p-0" align="start">
        <div className="max-h-64 overflow-y-auto p-1">
          {options.length === 0 ? (
            <p className="px-2 py-3 text-center text-sm text-muted-foreground">No options</p>
          ) : (
            options.map((opt) => {
              const isSelected = selected.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => toggle(opt)}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-muted focus:bg-muted focus:outline-none"
                >
                  <span
                    className={cn(
                      'flex size-4 shrink-0 items-center justify-center border border-input',
                      isSelected && 'border-primary bg-primary text-primary-foreground',
                    )}
                  >
                    {isSelected ? <Check className="size-3" aria-hidden="true" /> : null}
                  </span>
                  <span className="truncate">{opt}</span>
                </button>
              );
            })
          )}
        </div>
        {selected.length > 0 ? (
          <div className="border-t border-border p-1">
            <Button variant="ghost" size="sm" className="w-full justify-center text-xs" onClick={() => onChange([])}>
              Clear
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
